import React, { memo } from 'react'

import { Button, Icon, ChatAI } from '@cgtrader/triangle-ui/dist/src/ssr-index'

import { VERSION_KIND } from 'Pods/ThreeDStudio/Components/ImageAssistantPanel/imageRun'
import { iconSize } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import Composer from 'Pods/ThreeDStudio/Shared/Composer/Composer'

import { EMPTY_ARRAY, NOOP } from 'Utils/stableDefaults'

import MaskCanvas from './Components/Canvas/MaskCanvas'
import PromptViewer from './Components/PromptViewer/PromptViewer'
import Toolbar from './Components/Toolbar/Toolbar'
import useEditCanvas from './useEditCanvas'

const generateNewIcon = <Icon svgComponent={ChatAI} size={iconSize} notSpaced />

// Holds the live editing session. Remounted (via the key in EditTab) whenever a
// new image is loaded, so its seeded state resets cleanly; stays mounted across
// tab switches so the work is preserved.
function EditCanvas() {
  const t = useTranslation()
  const {
    canvas,
    currentImage,
    versions,
    versionCount,
    prompt,
    attachments,
    isHistoryOpen,
    isSending,
    isSendDisabled,
    isUploadDisabled,
    fileInputRef,
    handlePromptChange,
    handleUploadClick,
    handleRemoveAttachment,
    handleFilesChange,
    handleSend,
    handleToggleHistory,
    handleCloseHistory,
    handleSelectVersion,
    handleStartNew,
  } = useEditCanvas()

  return (
    <div className="image-assistant-edit-tab__edit">
      <div className="image-assistant-edit-tab__edit-scroll">
        <Button
          type="secondary"
          size="small"
          variant="with-icon"
          actionType="button"
          customClass="image-assistant-edit-tab__generate-new"
          icon={generateNewIcon}
          onClick={handleStartNew}
        >
          {t('imageAssistantPanel.editTab.generateNew')}
        </Button>
        <div className="image-assistant-edit-tab__canvas-card">
          <Toolbar
            canUndo={canvas.canUndo}
            canRedo={canvas.canRedo}
            isErasing={canvas.isErasing}
            brushSize={canvas.brushSize}
            versions={versions}
            versionCount={versionCount}
            currentId={currentImage?.id}
            isHistoryOpen={isHistoryOpen}
            isSending={isSending}
            onUndo={canvas.undo}
            onRedo={canvas.redo}
            onToggleEraser={canvas.toggleEraser}
            onBrushSizeChange={canvas.setBrushSize}
            onToggleHistory={handleToggleHistory}
            onSelectVersion={handleSelectVersion}
            onCloseHistory={handleCloseHistory}
          />
          <MaskCanvas
            image={currentImage}
            canvasRef={canvas.canvasRef}
            containerRef={canvas.containerRef}
            brushSize={canvas.brushSize}
            isErasing={canvas.isErasing}
            isSending={isSending}
            overlay={currentImage?.prompt && currentImage.kind === VERSION_KIND.PROMPT
              ? <PromptViewer key={currentImage.id} prompt={currentImage.prompt} />
              : null}
            onPointerDown={canvas.handlePointerDown}
            onPointerMove={canvas.handlePointerMove}
            onPointerUp={canvas.handlePointerUp}
          />
        </div>
      </div>
      <Composer
        prompt={prompt}
        attachments={attachments}
        tags={EMPTY_ARRAY}
        fileInputRef={fileInputRef}
        sendLabel={t('imageAssistantPanel.actions.editImage')}
        placeholder={t('imageAssistantPanel.editTab.composer.placeholder')}
        isSendDisabled={isSendDisabled}
        isUploadDisabled={isUploadDisabled}
        isUploadHidden
        onPromptChange={handlePromptChange}
        onTagClick={NOOP}
        onUploadClick={handleUploadClick}
        onRemoveAttachment={handleRemoveAttachment}
        onFilesChange={handleFilesChange}
        onSend={handleSend}
      />
    </div>
  )
}

export default memo(EditCanvas)
